import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core'
import { MatPaginator } from '@angular/material/paginator'
import { MatTableDataSource } from '@angular/material/table'
import { Router } from '@angular/router'
import { UserContextService } from '@core/service/user-context.service'
import { SITUACOES_MATRICULA_DESATUALIZADA } from '@shared/models'
import {
  AnoResidenciaEnum,
  MatriculaHistoricoPayloadModel,
  MatriculaHistoricoRowModel,
  ResidenteMatriculadoDetailsModel,
  SituacaoMatriculaDomainModel,
  SituacaoMatriculaEnum,
} from '@shared/models/backend'
import moment from 'moment'
import { merge, of } from 'rxjs'
import { catchError, map, startWith, switchMap } from 'rxjs/operators'
import { ResidenteHistoricoSituacoesService } from './residente-historico-situacoes.service'
import { MatDialogConfig } from '@angular/material/dialog'
import { AlertService } from '@app/alert/service/alert.service'
import { ModalService } from '@app/modal/modal.service'
import { SituacaoResidenteService } from '@app/modules/programa/situacao-residente/service/situacao-residente.service'
import { VisualizarResidenteService } from '@app/modules/programa/visualizar-residente/service/visualizar-residente.service'

@Component({
  selector: 'app-residente-historico-situacoes',
  templateUrl: './residente-historico-situacoes.component.html',
  styleUrls: ['./residente-historico-situacoes.component.scss'],
})
export class ResidenteHistoricoSituacoesComponent implements OnInit, AfterViewInit {
  dataSource = new MatTableDataSource()

  @ViewChild(MatPaginator)
  paginator: MatPaginator

  resultsLength = 0
  isLoadingResults = false

  @Input()
  details: ResidenteMatriculadoDetailsModel

  @Input()
  showPrograma = false

  displayedColumns: string[]

  constructor(
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private userContext: UserContextService,
    private residenteHistoricoService: ResidenteHistoricoSituacoesService,
    private alertService: AlertService,
    private modalService: ModalService,
    private situacaoResidenteService: SituacaoResidenteService,
    private visualizarResidenteService: VisualizarResidenteService
  ) {}

  canEdit(row: MatriculaHistoricoRowModel) {
    const situacaoMatricula = row.situacaoMatricula
    const notAllowed = [
      SituacaoMatriculaEnum.FERIAS,
      SituacaoMatriculaEnum.PRORROGACAO_LICENCA,
      SituacaoMatriculaEnum.MATRICULADO,
      SituacaoMatriculaEnum.DESISTENTE_DESLIGADO,
      SituacaoMatriculaEnum.TRANSFERIDO,
      SituacaoMatriculaEnum.MATRICULA_CANCELADA,
      SituacaoMatriculaEnum.RESIDENCIA_CONCLUIDA,
    ]

    return !notAllowed.includes(situacaoMatricula.id) && !row.dataFinalizacao
  }

  canFinalizar(row: MatriculaHistoricoRowModel) {
    const situacaoMatricula = row.situacaoMatricula
    const notAllowed = [
      SituacaoMatriculaEnum.DESISTENTE_DESLIGADO,
      SituacaoMatriculaEnum.TRANSFERIDO,
      SituacaoMatriculaEnum.MATRICULADO,
      SituacaoMatriculaEnum.MATRICULA_CANCELADA,
      SituacaoMatriculaEnum.RESIDENCIA_CONCLUIDA,
    ]

    return !notAllowed.includes(situacaoMatricula.id) && !row.dataFinalizacao
  }

  canExcluir(row: MatriculaHistoricoRowModel) {
    return !row.dataFinalizacao && this.userContext.isAdministrador() && this.dataSource.data.length > 1
  }

  podemEditarSituacao() {
    return this.userContext.isSupervisor() || this.userContext.isCoordenador() || this.userContext.isAdministrador()
  }

  onExcluirSituacao(historico: MatriculaHistoricoRowModel) {
    const dialogConfig = {
      title: 'Deseja excluir a situação?',
      message: `Confirme a exclusão da situação "${historico.situacaoMatricula.descricao}" do residente.`,
      primaryOption: 'EXCLUIR',
    }

    const callback = () =>
      this.situacaoResidenteService
        .excluirSituacao(this.details?.residenteMatriculado?.matriculaId, historico.matriculaHistoricoId)
        .subscribe(
          (res) => {
            this.alertService.showSuccess('A situação foi excluída com sucesso')
            this.ngAfterViewInit()
            this.visualizarResidenteService.refreshResidente(
              this.programaHashId,
              this.details.residente.matriculaId
            )
          },
          (error) => {
            this.alertService.showError('Houve um erro ao excluir a situação')
          }
        )

    this.confirm(dialogConfig, callback)
  }

  private confirm(data, onConfirmCallback) {
    const dialogConfig = new MatDialogConfig()
    dialogConfig.data = {
      secondaryOption: 'CANCELAR',
      ...data,
    }

    dialogConfig.width = '40em'
    dialogConfig.height = 'auto'

    const confirmModal = this.modalService.openConfirmModal(dialogConfig)

    confirmModal.afterClosed().subscribe((hasConfirmed: boolean) => {
      if (hasConfirmed) {
        onConfirmCallback()
      }
    })

    return confirmModal
  }

  onRegistrarSituacao() {
    this.router.navigateByUrl(
      `/programas/${this.programaHashId}/residentes/${this.details?.residenteMatriculado?.matriculaId}/situacao/registrar`
    )
  }

  onEditarSituacao(situacaoResidenteId: number) {
    this.router.navigateByUrl(
      `/programas/${this.programaHashId}/residentes/${this.details?.residenteMatriculado?.matriculaId}/situacao/editar/${situacaoResidenteId}`
    )
  }

  onVisualizarSituacao(situacaoResidenteId: number) {
    this.router.navigateByUrl(
      `/programas/${this.programaHashId}/residentes/${this.details?.residenteMatriculado?.matriculaId}/situacao/visualizar/${situacaoResidenteId}`
    )
  }

  onFinalizarSituacao(situacaoResidenteId: number) {
    this.router.navigateByUrl(
      `/programas/${this.programaHashId}/residentes/${this.details?.residenteMatriculado?.matriculaId}/situacao/finalizar/${situacaoResidenteId}`
    )
  }

  isRegistrarSituacaoEnabled() {
    return (
      SituacaoMatriculaEnum.MATRICULADO === this.details.residenteMatriculado.situacaoMatricula.id &&
      AnoResidenciaEnum.INTEGRALIZACAO !== this.details.residenteMatriculado.anoResidencia.id
    )
  }

  ngOnInit(): void {
    this.displayedColumns = [
      'dataCadastro',
      ...(this.showPrograma ? ['nomePrograma'] : []),
      'anoResidencia',
      'situacaoMatricula',
      'dataOcorrencia',
      'dataTermino',
      'dataFinalizacao',
      'actions',
    ]
  }

  ngAfterViewInit() {
    merge(this.paginator.page)
      .pipe(
        startWith({}),
        switchMap(() => {
          this.isLoadingResults = true
          this.cdRef.detectChanges()
          return this.residenteHistoricoService
            .getHistoricoSituacoesMatricula(
              this.details?.residenteMatriculado?.matriculaId,
              this.paginator.pageIndex,
              this.paginator.pageSize
            )
            .pipe(
              catchError((e) => {
                return this.emptyTable()
              })
            )
        }),
        map((data) => {
          this.isLoadingResults = false
          this.resultsLength = data.pageInfo.totalElements
          return data.matriculaHistoricos
        })
      )
      .subscribe((historico) => {
        this.dataSource.data = historico.filter(
          (situacaoResidente) =>
            !(
              (situacaoResidente.situacaoMatricula.id === SituacaoMatriculaEnum.PRORROGACAO_LICENCA &&
                moment(situacaoResidente.dataOcorrencia) > moment().startOf('day')) ||
              (situacaoResidente.matriculaAntigaId != null &&
                situacaoResidente.situacaoMatricula.id === SituacaoMatriculaEnum.MATRICULADO)
            )
        )
        this.cdRef.markForCheck()
      })
  }

  private emptyTable() {
    return of({
      matriculaHistoricos: [],
      pageInfo: {
        totalElements: 0,
        totalPages: 0,
      },
    } as MatriculaHistoricoPayloadModel)
  }

  get programaHashId() {
    let programa: string
    if (this.details?.programa?.hashPrograma) {
      programa = this.details?.programa?.hashPrograma
    } else {
      programa = this.details?.programa?.id.toString()
    }

    return programa
  }

  get labelAlertaRegistroSituacao() {
    const situacaoId = this.details.residenteMatriculado.situacaoMatricula.id
    if (
      [
        SituacaoMatriculaEnum.DESISTENTE_DESLIGADO,
        SituacaoMatriculaEnum.TRANSFERIDO,
        SituacaoMatriculaEnum.MATRICULA_CANCELADA,
        SituacaoMatriculaEnum.PENDENTE_CONCLUSAO_TCC,
        SituacaoMatriculaEnum.RESIDENCIA_CONCLUIDA,
      ].includes(situacaoId)
    ) {
      return 'Não é possível registrar novas situações para este residente, pois sua matrícula foi inativada.'
    }
    if (this.details.residenteMatriculado.anoResidencia.id === AnoResidenciaEnum.INTEGRALIZACAO) {
      return 'Não é possível registrar uma nova situação para residentes em integralização.'
    }
    return 'Para registrar uma nova situação é necessário finalizar a anterior.'
  }

  getSituacaoResidenteTag(situacao: SituacaoMatriculaDomainModel) {
    switch (situacao?.id) {
      case SituacaoMatriculaEnum.AGUARDANDO_CADASTRO:
        return 'blue'
      case SituacaoMatriculaEnum.MATRICULADO:
      case SituacaoMatriculaEnum.FERIAS:
        return 'teal'
      case SituacaoMatriculaEnum.MATRICULA_CANCELADA:
      case SituacaoMatriculaEnum.SUSPENSO:
        return 'red'
      case SituacaoMatriculaEnum.DESISTENTE_DESLIGADO:
      case SituacaoMatriculaEnum.RESIDENCIA_CONCLUIDA:
      case SituacaoMatriculaEnum.TRANSFERIDO:
      case SituacaoMatriculaEnum.PENDENTE_CONCLUSAO_TCC:
        return 'grey'
    }
    return 'warning'
  }

  isSituacaoResidenteDesatualizada(
    id: number,
    dataTermino: string,
    dataFinalizacao: string,
    prorrogacaoLicencaMaternidade: boolean
  ) {
    return (
      SITUACOES_MATRICULA_DESATUALIZADA.includes(id) &&
      moment(dataTermino) < moment().startOf('day') &&
      dataFinalizacao === null &&
      !prorrogacaoLicencaMaternidade
    )
  }

  isSituacaoProgramaAnterior(matriculaHistorico: MatriculaHistoricoRowModel): boolean {
    return matriculaHistorico.matriculaAntigaId != null
  }
}
